import { useNavigate } from 'react-router-dom';
import { Calendar, RotateCcw, Trash2 } from 'lucide-react';
import { giraffeTrip } from '@/lib/giraffePaths';
import ZEmptyState from '../shared/ZEmptyState';
import OverflowMenu from '../shared/OverflowMenu';

/**
 * GiraffeArchiveTab — renders trip cards for trips with status: 'archived'.
 *
 * Same card structure as the Active Trips tab but no progress bar (archived
 * trips are complete) and the overflow menu offers Unarchive and Delete.
 */
export default function GiraffeArchiveTab({ trips, onUnarchive, onDelete }) {
  const navigate = useNavigate();
  const archived = trips.filter(t => t.status === 'archived');

  if (archived.length === 0) {
    return (
      <ZEmptyState
        mark="G"
        accent="#10B981"
        message="No archived trips"
        hint="Archive a trip from the Active Trips overflow menu."
      />
    );
  }

  const getImageUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {archived.map((trip) => (
        <div
          key={trip.id}
          data-testid={`archived-trip-card-${trip.id}`}
          onClick={() => navigate(giraffeTrip(trip.id))}
          className="group relative bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden cursor-pointer hover:border-white/[0.10] transition-all duration-500"
          style={{ position: 'relative' }}
        >
          <div onClick={e => e.stopPropagation()}>
            <OverflowMenu items={[
              { icon: <RotateCcw size={16} strokeWidth={1.5} />, label: 'Unarchive', onClick: () => onUnarchive(trip) },
              { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => onDelete(trip), destructive: true },
            ]} />
          </div>
          <div className="h-36 relative overflow-hidden">
            {trip.cover_image ? (
              <img src={getImageUrl(trip.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ filter: 'grayscale(0.4) brightness(0.85)' }} />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-white/[0.02] via-[#0a0a0f] to-[#0a0a0f]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] to-transparent" />
            <div className="absolute top-3 left-3">
              <span className="text-xs font-mono tracking-wider uppercase px-2 py-1 rounded-full bg-white/[0.05] text-[#6b7084]">
                Archived
              </span>
            </div>
          </div>
          <div className="p-5 -mt-4 relative z-10">
            <h3 className="text-lg text-[#f0f0f5] font-serif mb-1">{trip.title}</h3>
            {trip.description && (
              <p className="text-[#b0b4c0] font-light text-sm line-clamp-2 mb-3">{trip.description}</p>
            )}
            <div className="flex items-center gap-4 text-[#b0b4c0]/70">
              {trip.start_date && (
                <span className="flex items-center gap-1 text-xs font-mono">
                  <Calendar size={12} />
                  {trip.start_date}{trip.end_date ? ` — ${trip.end_date}` : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
